import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, Save, X } from 'lucide-react';
import { EditCostBudgetForm } from './edit-cost-budget-form';
import { EditSalesBudgetForm } from './edit-sales-budget-form';
import { EditSalesBudgetFormTM } from './edit-sales-budget-form-tm';
import { EditResourceBudgetForm } from './edit-resource-budget-form';
import { BudgetRevisionPanel } from './budget-revision-panel';
import type { BudgetData, ProjectType, CostCategory, SalesCategory } from '@/types/budget.types';
import { fetchDefaultCategoriesAsCostCategories } from '@/lib/data-service';


// Mock available sales categories for T&M projects
// Updated to include test scenarios for delete validation:
// - Labour: Has actual costs (cannot delete)
// - Materials: Has budget only (can delete with warning)
// - Equipment: Has both budget and actuals (cannot delete)
// - Subcontractors: Has budget only (can delete with warning)
// - Travel & Expenses: Empty (can delete immediately)
// - Software & Licenses: Has actuals (cannot delete)
// - Overhead: Empty (can delete immediately)
// - Contingency: Has actuals (cannot delete)
const MOCK_AVAILABLE_SALES_CATEGORIES: SalesCategory[] = [
  {
    id: 1,
    name: 'Labour',
    type: 'labour',
    budget: 350000,
    actual: 231000, // HAS ACTUALS - Cannot delete
    remaining: 119000,
    percentBilled: 66,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 2,
    name: 'Materials',
    type: 'expenses',
    budget: 104000, // NO ACTUALS - Can delete with warning
    actual: 0,
    remaining: 104000,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 3,
    name: 'Equipment',
    type: 'expenses',
    budget: 78000,
    actual: 49400, // HAS ACTUALS - Cannot delete
    remaining: 28600,
    percentBilled: 63,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 4,
    name: 'Subcontractors',
    type: 'subcontractors',
    budget: 84000, // NO ACTUALS - Can delete with warning
    actual: 0,
    remaining: 84000,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 5,
    name: 'Travel & Expenses',
    type: 'expenses',
    budget: 0, // NO BUDGET - Can delete immediately
    actual: 0,
    remaining: 0,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 6,
    name: 'Software & Licenses',
    type: 'expenses',
    budget: 36400,
    actual: 23400, // HAS ACTUALS - Cannot delete
    remaining: 13000,
    percentBilled: 64,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 7,
    name: 'Overhead',
    type: 'expenses',
    budget: 0, // NO BUDGET - Can delete immediately
    actual: 0,
    remaining: 0,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 8,
    name: 'Contingency',
    type: 'expenses',
    budget: 19500,
    actual: 11700, // HAS ACTUALS - Cannot delete
    remaining: 7800,
    percentBilled: 60,
    scope: 'global',
    isCustom: false,
  },
];

interface EditBudgetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budgetData: BudgetData;
  projectType: ProjectType;
  onSave: (updatedBudget: Partial<BudgetData>, revisionReason: string, saveAsBaseline: boolean) => Promise<void>;
  initialTab?: 'cost' | 'sales' | 'resource';
  projectStartDate?: string;
  projectEndDate?: string;
}

export function EditBudgetModal({
  open,
  onOpenChange,
  budgetData,
  projectType,
  onSave,
  initialTab = 'cost',
  projectStartDate,
  projectEndDate,
}: EditBudgetModalProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Track changes for each budget type
  const [costBudgetChanges, setCostBudgetChanges] = useState<any>(null);
  const [salesBudgetChanges, setSalesBudgetChanges] = useState<any>(null);
  const [resourceBudgetChanges, setResourceBudgetChanges] = useState<any>(null);
  
  // Revision details
  const [revisionReason, setRevisionReason] = useState('');
  const [saveAsBaseline, setSaveAsBaseline] = useState(false);

  // Fetch default GlobalCategories to auto-populate when no cost budget categories exist
  const [defaultGlobalCategories, setDefaultGlobalCategories] = useState<CostCategory[]>([]);
  
  useEffect(() => {
    if (open && budgetData.costBudget?.categories?.length === 0) {
      fetchDefaultCategoriesAsCostCategories()
        .then(categories => {
          setDefaultGlobalCategories(categories);
        })
        .catch(err => {
          console.warn('[EditBudgetModal] Failed to load default GlobalCategories:', err);
        });
    }
  }, [open, budgetData.costBudget?.categories?.length]);

  // Reset activeTab when modal opens with a new initialTab
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  // Determine which tabs should be visible based on the context
  const visibleTabs = (() => {
    switch (initialTab) {
      case 'cost':
        return ['cost', 'revision'];
      case 'sales':
        return ['sales']; // Removed revision tab - will come back to revisions later
      case 'resource':
        return ['resource', 'revision'];
      default:
        return ['cost', 'sales', 'resource', 'revision'];
    }
  })();

  // Calculate total change percentage
  const calculateChangePercentage = () => {
    if (!costBudgetChanges) return 0;
    
    const originalTotal = budgetData.costBudget?.totals?.budget || 0;
    const newTotal = costBudgetChanges.totals?.budget || originalTotal;
    
    if (originalTotal === 0) return 0;
    
    return Math.abs(((newTotal - originalTotal) / originalTotal) * 100);
  };

  const changePercentage = calculateChangePercentage();
  const requiresApproval = changePercentage > 5;

  const handleSave = async (asDraft: boolean = false) => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate revision reason if required
      if (!asDraft && requiresApproval && !revisionReason.trim()) {
        setError('Please provide a reason for this budget change (required for changes >5%)');
        return;
      }

      // Validate sales budget for Fixed Price projects
      if (salesBudgetChanges && projectType === 'fixed-price') {
        const contractValue = salesBudgetChanges.contractValue ?? budgetData.salesBudget?.contractValue ?? 0;
        if (contractValue <= 0) {
          setError('Contract value must be greater than zero for Fixed Price projects');
          return;
        }

        // Validate payment schedule items have name and due date
        const milestones = salesBudgetChanges.milestones ?? budgetData.salesBudget?.milestones ?? [];
        if (milestones.length > 0) {
          const invalidMilestones = milestones.filter((m: any) => !m.name?.trim() || !m.dueDate);
          if (invalidMilestones.length > 0) {
            setError('All payment schedule items must have a name and due date');
            return;
          }
        }
      }

      // Combine all changes
      const updatedBudget: Partial<BudgetData> = {};
      
      if (costBudgetChanges) {
        updatedBudget.costBudget = costBudgetChanges;
      }
      
      if (salesBudgetChanges) {
        updatedBudget.salesBudget = salesBudgetChanges;
      }
      
      if (resourceBudgetChanges) {
        updatedBudget.resourceBudget = resourceBudgetChanges;
      }

      await onSave(updatedBudget, revisionReason, saveAsBaseline);
      
      // Reset state
      setIsDirty(false);
      setCostBudgetChanges(null);
      setSalesBudgetChanges(null);
      setResourceBudgetChanges(null);
      setRevisionReason('');
      setSaveAsBaseline(false);
      
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelDialog(true);
      return;
    }
    closeModal();
  };

  const closeModal = () => {
    // Reset state
    setIsDirty(false);
    setCostBudgetChanges(null);
    setSalesBudgetChanges(null);
    setResourceBudgetChanges(null);
    setRevisionReason('');
    setSaveAsBaseline(false);
    setError(null);
    setShowCancelDialog(false);
    onOpenChange(false);
  };

  const handleConfirmCancel = () => {
    closeModal();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // User is trying to close the modal
      handleCancel();
    } else {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Resource Budget</DialogTitle>
          <DialogDescription>
            Update budget values for {budgetData.projectName}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Only show tabs if there's more than one visible tab */}
        {visibleTabs.length > 1 ? (
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'cost' | 'sales' | 'resource')} className="w-full">
            <TabsList className={`grid w-full grid-cols-${visibleTabs.length}`}>
              {visibleTabs.includes('cost') && (
                <TabsTrigger value="cost">Cost Budget</TabsTrigger>
              )}
              {visibleTabs.includes('sales') && (
                <TabsTrigger value="sales">Sales Budget</TabsTrigger>
              )}
              {visibleTabs.includes('resource') && (
                <TabsTrigger value="resource">Resource Budget</TabsTrigger>
              )}
              {visibleTabs.includes('revision') && (
                <TabsTrigger value="revision">Revision Details</TabsTrigger>
              )}
            </TabsList>

            {visibleTabs.includes('cost') && (
              <TabsContent value="cost" className="space-y-4">
                <EditCostBudgetForm
                  costBudget={budgetData.costBudget}
                  onChange={(changes) => {
                    setCostBudgetChanges(changes);
                    setIsDirty(true);
                  }}
                    availableCategories={budgetData.costBudget?.categories?.length > 0 
                      ? budgetData.costBudget.categories 
                      : defaultGlobalCategories}
                  resourceBudget={budgetData.resourceBudget}
                />
              </TabsContent>
            )}

            {visibleTabs.includes('sales') && (
              <TabsContent value="sales" className="space-y-4">
                {projectType === 'time-and-materials' ? (
                  <>
                    <EditSalesBudgetFormTM
                      projectId={budgetData.projectId}
                      salesBudget={budgetData.salesBudget}
                      onChange={(changes) => {
                        setSalesBudgetChanges(changes);
                        setIsDirty(true);
                      }}
                      projectStartDate={projectStartDate}
                      projectEndDate={projectEndDate}
                      availableCategories={MOCK_AVAILABLE_SALES_CATEGORIES}
                      resourceBudget={budgetData.resourceBudget}
                    />
                  </>
                ) : (
                  <EditSalesBudgetForm
                    projectId={budgetData.projectId}
                    salesBudget={budgetData.salesBudget}
                    projectType={projectType}
                    onChange={(changes) => {
                      setSalesBudgetChanges(changes);
                      setIsDirty(true);
                    }}
                  />
                )}
              </TabsContent>
            )}

            {visibleTabs.includes('resource') && (
              <TabsContent value="resource" className="space-y-4">
                <EditResourceBudgetForm
                  projectId={budgetData.projectId}
                  resourceBudget={budgetData.resourceBudget}
                  onChange={(changes) => {
                    setResourceBudgetChanges(changes);
                    setIsDirty(true);
                  }}
                />
              </TabsContent>
            )}

            {visibleTabs.includes('revision') && (
              <TabsContent value="revision" className="space-y-4">
                <BudgetRevisionPanel
                  changePercentage={changePercentage}
                  requiresApproval={requiresApproval}
                  revisionReason={revisionReason}
                  onRevisionReasonChange={setRevisionReason}
                  saveAsBaseline={saveAsBaseline}
                  onSaveAsBaselineChange={setSaveAsBaseline}
                  costBudgetChanges={costBudgetChanges}
                  salesBudgetChanges={salesBudgetChanges}
                  resourceBudgetChanges={resourceBudgetChanges}
                  originalBudget={budgetData}
                />
              </TabsContent>
            )}
          </Tabs>
        ) : (
          /* Single tab - render content directly without tabs UI */
          <div className="space-y-4">
            {visibleTabs.includes('cost') && (
              <EditCostBudgetForm
                costBudget={budgetData.costBudget}
                onChange={(changes) => {
                  setCostBudgetChanges(changes);
                  setIsDirty(true);
                }}
                availableCategories={budgetData.costBudget?.categories?.length > 0 
                  ? budgetData.costBudget.categories 
                  : defaultGlobalCategories}
                resourceBudget={budgetData.resourceBudget}
              />
            )}

            {visibleTabs.includes('sales') && (
              <>
                {projectType === 'time-and-materials' ? (
                  <>
                    <EditSalesBudgetFormTM
                      projectId={budgetData.projectId}
                      salesBudget={budgetData.salesBudget}
                      onChange={(changes) => {
                        setSalesBudgetChanges(changes);
                        setIsDirty(true);
                      }}
                      projectStartDate={projectStartDate}
                      projectEndDate={projectEndDate}
                      availableCategories={MOCK_AVAILABLE_SALES_CATEGORIES}
                      resourceBudget={budgetData.resourceBudget}
                    />
                  </>
                ) : (
                  <EditSalesBudgetForm
                    projectId={budgetData.projectId}
                    salesBudget={budgetData.salesBudget}
                    projectType={projectType}
                    onChange={(changes) => {
                      setSalesBudgetChanges(changes);
                      setIsDirty(true);
                    }}
                  />
                )}
              </>
            )}

            {visibleTabs.includes('resource') && (
              <EditResourceBudgetForm
                projectId={budgetData.projectId}
                resourceBudget={budgetData.resourceBudget}
                onChange={(changes) => {
                  setResourceBudgetChanges(changes);
                  setIsDirty(true);
                }}
              />
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {isDirty && (
              <span className="text-warning-600">
                • Unsaved changes ({changePercentage.toFixed(1)}% change)
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              onClick={() => handleSave(false)}
              disabled={!isDirty || isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              {requiresApproval ? 'Submit for Approval' : 'Save Budget'}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
